import { useMemo, useEffect, useCallback } from "react";
import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import { useNavigate } from "react-router-dom";
import { getOptimizedImageForType } from "../../utils/cms.utils";
import OptimizedImage from "../common/OptimizedImage";
import ContentTag from "../../utils/tag";
import {
  getAgeRangePublicRoute,
  isContentItemAllowedForAgeRange,
} from "../../utils/ageRange.util";
import { useInputMode } from "../../utils/hooks/useInputMode";
import LazyRow from "../common/LazyRow";

// ============================================================================
// CONSTANTS
// ============================================================================

// Number of items per row
var ITEMS_PER_ROW = 5;

/**
 * Chunks an array into smaller arrays of specified size
 */
function chunkArray(array, size) {
  var result = [];
  for (var i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
}

var MovieItem = function (props) {
  var item = props.item || {};
  var onEnterPress = props.onEnterPress;

  var contentPhotos = item.contentPhotos;
  var tags = item.tags;
  var contentType = item.contentType;
  var _id = item._id;
  var kind = item.kind;

  var focusableResult = useFocusable({
    onEnterPress: onEnterPress,
    focusKey: "search_content_" + _id,
  });

  var focused = focusableResult.focused;
  var ref = focusableResult.ref;
  var focusSelf = focusableResult.focusSelf;

  var inputModeResult = useInputMode();
  var setMouseFocus = inputModeResult.setMouseFocus;
  var handleMouseEnter = useCallback(function () {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  var isSeries = contentType?.kind === "series" || kind === "series";

  // Get optimized image with LQIP and WebP support (w=200, q=95)
  var imageData = getOptimizedImageForType("verticalCover", contentPhotos, "TV", { width: 350, quality: 95, format: "jpeg" });

  return (
    <div className="legacy-child-grid-item">
      {isSeries && (
        <div className="legacy-child-grid-series-decoration">
          <img src="/serial_pseudo.svg" alt="" />
        </div>
      )}
      <div
        ref={ref}
        className={"legacy-child-grid-card-outer" + (focused ? " is-focused" : "")}
        onMouseEnter={handleMouseEnter}
        onClick={onEnterPress}
      >
        <div className="legacy-child-grid-card legacy-child-grid-card-dark">
          <div className="legacy-child-grid-image">
            <OptimizedImage
              src={imageData.src}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </div>
          {tags?.[0] && <ContentTag label={tags[0]} />}
          <div className="legacy-child-grid-overlay">
            <span className="legacy-child-grid-title">{item.title}</span>
            <span className="legacy-child-grid-year">{item.year}</span>
          </div>
        </div>
        <div className="legacy-child-grid-border" />
      </div>
    </div>
  );
};

/**
 * Single row of movie items with its own focus context
 */
var MovieRow = function (props) {
  var items = props.items;
  var onFocus = props.onFocus;
  var navigate = props.navigate;

  var focusableResult = useFocusable({
    onFocus,
  });
  var ref = focusableResult.ref;
  var focusKey = focusableResult.focusKey;

  return (
    <FocusContext.Provider value={focusKey}>
      <div className="legacy-child-grid-row-container" ref={ref}>
        {items.map(function (item, index) {
          var contentKind = item.contentType?.kind || "movie";
          return (
            <MovieItem
              item={item}
              key={item._id + "_" + index}
              onEnterPress={function () {
                navigate("/" + contentKind + "/" + item.slug);
              }}
            />
          );
        })}
      </div>
    </FocusContext.Provider>
  );
};

export var MoviesSlider = function (props) {
  var movies = props.movies;
  var onFocus = props.onFocus;
  var shouldFocusSelf = props.shouldFocusSelf;

  var ageRange = getAgeRangePublicRoute();
  var _items = movies.filter(function (x) {
    return isContentItemAllowedForAgeRange(x, ageRange);
  });

  var focusableResult = useFocusable();
  var ref = focusableResult.ref;
  var focusKey = focusableResult.focusKey;
  var focusSelf = focusableResult.focusSelf;

  var navigate = useNavigate();

  useEffect(
    function () {
      if (shouldFocusSelf === true) {
        focusSelf();
      }
    },
    [shouldFocusSelf, focusSelf]
  );

  // Chunk items into rows
  var chunkedItems = useMemo(
    function () {
      if (!_items || _items.length === 0) return [];
      return chunkArray(_items, ITEMS_PER_ROW);
    },
    [_items]
  );

  if (!_items || _items.length < 1) return null;

  return (
    <FocusContext.Provider value={focusKey}>
      <div className="legacy-child-grid-row" ref={ref}>
        {chunkedItems.map(function (rowItems, rowIndex) {
          return (
            <LazyRow key={"search_row_" + rowIndex} index={rowIndex}>
              <MovieRow
                items={rowItems}
                onFocus={onFocus}
                navigate={navigate}
              />
            </LazyRow>
          );
        })}
      </div>
    </FocusContext.Provider>
  );
};
